/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { Utils } from "../../utils/utils";
import { GTSLib } from "../../utils/gts.lib";
import elementResizeEvent from "element-resize-event";
import { PluginManager } from "../../utils/PluginManager";
import { v4 } from "uuid";
import { isEqual } from "lodash";
export class DiscoveryTileResultComponent {
    constructor() {
        this.innerVars = {};
        this.result = undefined;
        this.type = undefined;
        this.start = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = '';
        this.url = undefined;
        this.chartTitle = undefined;
        this.chartDescription = undefined;
        this.language = 'warpscript';
        this.vars = '{}';
        this.execTime = 0;
        this.bgColor = undefined;
        this.fontColor = undefined;
        this.innerResult = undefined;
        this.innerOptions = new Param();
        this.innerStyle = undefined;
        this.innerType = undefined;
        this.innerTitle = undefined;
    }
    updateType(newValue) {
        if (newValue !== this.innerType) {
            setTimeout(() => {
                this.innerType = this.type;
                this.selfType.emit(this.innerType);
            });
        }
    }
    updateRes(newValue) {
        var _a;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['updateRes'], 'could be huge');
        this.innerResult = GTSLib.getData(newValue);
        this.parseResult();
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        if (!!this.options && typeof this.options === 'string') {
            const opts = JSON.parse(this.options);
            if (!isEqual(opts, this.innerOptions)) {
                this.innerOptions = opts;
            }
        }
        else if (!isEqual(this.options, this.innerOptions)) {
            this.innerOptions = Object.assign({}, this.options);
        }
        if (this.LOG) {
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue });
        }
    }
    varsUpdate(newValue, oldValue) {
        var _a;
        if (!!this.vars && typeof this.vars === 'string') {
            const vars = JSON.parse(this.vars);
            if (!isEqual(vars, this.innerVars)) {
                this.innerVars = vars;
            }
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['varsUpdate'], { vars: this.vars, newValue, oldValue });
    }
    discoveryEventHandler(event) {
        var _a, _b, _c;
        if (!((_a = this.innerOptions) === null || _a === void 0 ? void 0 : _a.eventHandler)) {
            return;
        }
        const res = Utils.parseEventData(event.detail, ((_b = this.innerOptions) === null || _b === void 0 ? void 0 : _b.eventHandler) || '', this.componentId);
        if (res.hasEvent) {
            (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['discoveryEventHandler'], {
                type: event.detail.type,
                event: event.detail,
            });
            if (res.data) {
                this.innerResult = res.data;
                this.parseResult();
            }
            if (res.style) {
                this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
            }
            if (res.zoom) {
                void this.setZoom(res.zoom).then(() => {
                    // empty
                });
            }
            if (res.focus) {
                if (!!res.focus.date) {
                    void this.setFocus(res.focus.name, res.focus.date, res.focus.value).then(() => {
                        // empty
                    });
                }
                else {
                    void this.unFocus().then(() => {
                        // empty
                    });
                }
            }
            if (res.margin) {
                this.innerOptions = Object.assign(Object.assign({}, this.innerOptions), { leftMargin: res.margin });
            }
            if (res.bounds) {
                this.innerOptions = Object.assign(Object.assign({}, this.innerOptions), { bounds: Object.assign(Object.assign({}, this.innerOptions.bounds), { minDate: res.bounds.min, maxDate: res.bounds.max }) });
            }
        }
    }
    onDrawHandler() {
        if (!!this.tile) {
            if (!!this.tile.resize) {
                (this.tile).resize();
            }
        }
    }
    onLeftMarginComputed(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'margin')
            .forEach(e => {
            this.discoveryEvent.emit({ source: this.componentId, type: 'margin', tags: e.tags, value: event.detail });
        });
    }
    onTimeBounds(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'bounds')
            .forEach(e => {
            this.discoveryEvent.emit({ source: this.componentId, type: 'bounds', tags: e.tags, value: event.detail });
        });
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a, _b, _c;
        this.LOG = new Logger(DiscoveryTileResultComponent, this.debug);
        this.componentId = this.el.id || v4();
        this.innerType = this.type;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
        });
        let options = new Param();
        this.innerResult = GTSLib.getData(this.result);
        if (!!this.options && typeof this.options === 'string' && this.options !== 'undefined') {
            options = Object.assign(Object.assign({}, options), JSON.parse(this.options));
        }
        else {
            options = Object.assign(Object.assign({}, options), (this.options || {}));
        }
        options = Utils.mergeDeep(options || {}, this.innerResult.globalParams || {});
        this.innerOptions = Object.assign({}, options);
        this.innerVars = JSON.parse(this.vars || '{}');
        this.innerType = ((_b = this.innerResult.globalParams) === null || _b === void 0 ? void 0 : _b.type) || this.innerOptions.type || this.innerType;
        this.selfType.emit(this.innerType);
        (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['componentWillLoad 2'], {
            type: this.innerType,
            options: this.innerOptions,
        });
    }
    // noinspection JSUnusedGlobalSymbols
    componentDidLoad() {
        this.parseResult();
        elementResizeEvent.unbind(this.tileElem);
        elementResizeEvent(this.tileElem, () => this.resize());
    }
    // noinspection JSUnusedGlobalSymbols
    disconnectedCallback() {
        elementResizeEvent.unbind(this.tileElem);
    }
    handleZoom(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'zoom')
            .forEach(e => {
            e.value = event.detail;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    handleDataPointOver(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'focus')
            .forEach(e => {
            e.value = event.detail;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    handleDataSelected(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'selected')
            .forEach(e => {
            e.value = event.detail;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    handlePoi(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'poi')
            .forEach(e => {
            e.value = event.detail;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    handleGeoBounds(event) {
        (this.innerResult.events || [])
            .filter(e => e.type === 'bounds')
            .forEach(e => {
            e.value = event.detail;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    getView() {
        var _a, _b;
        switch (this.innerType) {
            case 'line':
            case 'area':
            case 'scatter':
            case 'spline-area':
            case 'step-area':
            case 'spline':
            case 'step':
            case 'step-after':
            case 'step-before':
                return h("discovery-line", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, debug: this.debug, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), onPoi: event => this.handlePoi(event), ref: el => this.tile = el || this.tile, id: this.componentId });
            case 'annotation':
                return h("discovery-annotation", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), onPoi: event => this.handlePoi(event), debug: this.debug, id: this.componentId });
            case 'bar':
                return h("discovery-bar", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), onPoi: event => this.handlePoi(event), debug: this.debug, id: this.componentId });
            case 'bar-polar':
                return h("discovery-bar-polar", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'boxplot':
                return h("discovery-boxplot", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'display':
                return h("discovery-display", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'map':
                return h("discovery-map", { result: this.innerResult, type: this.innerType, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), onGeoBounds: event => this.handleGeoBounds(event), onPoi: event => this.handlePoi(event), debug: this.debug, id: this.componentId });
            case 'image':
                return h("discovery-image", { result: this.innerResult, type: this.innerType, options: this.innerOptions, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'button':
            case 'button:radio':
                return h("discovery-button", { result: this.innerResult, url: this.url, type: this.innerType, options: this.innerOptions, ref: el => this.tile = el || this.tile, vars: JSON.stringify(this.innerVars), language: this.language, debug: this.debug, id: this.componentId });
            case 'gauge':
            case 'circle':
            case 'compass':
                return h("discovery-gauge", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'linear-gauge':
                return h("discovery-linear-gauge", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), debug: this.debug, id: this.componentId });
            case 'pie':
            case 'doughnut':
            case 'rose':
                return h("discovery-pie", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'tabular':
                return h("discovery-tabular", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'svg':
                return h("discovery-svg", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'input:text':
            case 'input:autocomplete':
            case 'input:list':
            case 'input:secret':
            case 'input:slider':
            case 'input:date':
            case 'input:date-range':
            case 'input:multi':
            case 'input:multi-cb':
            case 'input:chips':
            case 'input:chips-autocomplete':
                return h("discovery-input", { result: this.innerResult, type: this.innerType, options: this.innerOptions, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'hidden':
                return h("discovery-hidden", { result: this.innerResult, type: this.innerType, options: this.innerOptions, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
            case 'calendar':
                return h("discovery-calendar", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'heatmap':
                return h("discovery-heatmap", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'profile':
                return h("discovery-profile", { result: this.innerResult, type: this.innerType, unit: this.unit, options: this.innerOptions, ref: el => this.tile = el || this.tile, onDataZoom: event => this.handleZoom(event), onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataSelected(event), debug: this.debug, id: this.componentId });
            case 'dashboard':
            case 'dashboard:flex':
            case 'dashboard:scada':
                const sub = this.innerType.split(':');
                let dashBoardType = 'dashboard';
                if (sub.length > 1) {
                    dashBoardType = sub[1];
                }
                return h("discovery-dashboard", { warpscript: `<'
          ${JSON.stringify(GTSLib.getData(this.innerResult).data)}
'>
JSON-> 0 GET`, vars: this.innerVars, type: dashBoardType, url: this.url, options: this.innerOptions, ref: el => this.tile = el || this.tile, onRendered: () => this.draw.emit(), debug: this.debug, id: this.componentId });
            default:
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['getView'], PluginManager.getInstance().registry);
                if (PluginManager.getInstance().has(this.innerType)) {
                    (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['getView', 'type'], this.innerType);
                    const Tag = PluginManager.getInstance().get(this.innerType).tag;
                    return h(Tag, { result: this.innerResult, type: this.innerType, options: this.innerOptions, height: this.height, width: this.width, ref: el => this.tile = el || this.tile, debug: this.debug, id: this.componentId });
                }
                return '';
        }
    }
    async resize() {
        setTimeout(() => {
            if (!!this.tile && !!this.tile.resize) {
                (this.tile).resize();
            }
        });
        return Promise.resolve();
    }
    async setZoom(dataZoom) {
        if (this.tile) {
            (this.tile).setZoom(dataZoom);
        }
        return Promise.resolve();
    }
    async show(regexp) {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['show']) {
            await (this.tile).show(regexp);
        }
    }
    async showById(id) {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['showById']) {
            await (this.tile).showById(id);
        }
    }
    async hide(regexp) {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['hide']) {
            await (this.tile).hide(regexp);
        }
    }
    async hideById(id) {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['hideById']) {
            await (this.tile).hideById(id);
        }
    }
    async setFocus(regexp, ts, value) {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['setFocus']) {
            await (this.tile).setFocus(regexp, ts, value);
        }
    }
    async unFocus() {
        /* eslint-disable dot-notation,@typescript-eslint/dot-notation */
        if (this.tile && this.tile['unFocus']) {
            await (this.tile).unFocus();
        }
    }
    async export(type = 'png') {
        /* eslint-disable dot-notation, @typescript-eslint/dot-notation */
        if (this.tile && this.tile['export']) {
            const dataUrl = await (this.tile).export(type);
            return { dataUrl, bgColor: Utils.getCSSColor(this.tileElem, '--warp-view-tile-background', '#fff') };
        }
        else {
            return undefined;
        }
    }
    render() {
        return [
            h("style", { key: '228e027a22bf543076ba2c2f09167f6e5014d940' }, this.generateStyle(this.innerStyle)),
            h("div", { key: '90452c161980040dd3ccaf33cba7952381664193', class: "discovery-tile", ref: el => this.tileElem = el, style: {
                    background: this.bgColor,
                    color: this.fontColor,
                    height: '100%', width: '100%',
                } }, this.innerTitle && this.innerTitle !== '' ? h("h2", { class: "tile-title", ref: el => this.title = el }, this.innerTitle || '') : '', this.chartDescription && this.chartDescription !== '' ? h("p", { class: "tile-desc", ref: el => this.desc = el }, this.chartDescription || '') : '', h("div", { key: '933f9205b7953927b5334e9061711db079e2ca66', class: "discovery-chart-wrapper", ref: (el) => this.wrapper = el }, this.getView())),
        ];
    }
    async parseEvents() {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseEvents'], { discoveryEvents: (((_b = this.innerResult) === null || _b === void 0 ? void 0 : _b.events) || []) });
        setTimeout(() => {
            var _a;
            return (((_a = this.innerResult) === null || _a === void 0 ? void 0 : _a.events) || [])
                .filter(e => e.value !== undefined)
                .filter(e => e.type !== 'zoom' && e.type !== 'margin' && e.type !== 'selected')
                .forEach(e => {
                var _a;
                if (this.LOG) {
                    (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseResult', 'emit'], { discoveryEvent: e });
                }
                this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
            });
        });
        return Promise.resolve();
    }
    parseResult() {
        var _a;
        setTimeout(() => {
            void (async () => {
                var _a, _b, _c, _d;
                this.unit = this.options.unit || this.unit;
                this.innerOptions = Object.assign(Object.assign({}, this.innerOptions), ((_a = this.innerResult) === null || _a === void 0 ? void 0 : _a.globalParams) || {});
                this.innerType = ((_c = (_b = this.innerResult) === null || _b === void 0 ? void 0 : _b.globalParams) === null || _c === void 0 ? void 0 : _c.type) || this.innerType;
                this.selfType.emit(this.innerType);
                this.innerTitle = ((_d = this.innerOptions) === null || _d === void 0 ? void 0 : _d.title) || this.chartTitle || '';
                this.handleCSSColors();
                await this.parseEvents();
            })();
        });
        if (this.LOG) {
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseResult'], { type: this.innerType, options: this.options });
        }
    }
    generateStyle(styles) {
        this.innerStyles = Object.assign(Object.assign(Object.assign({}, this.innerStyles), styles), this.innerOptions.customStyles || {});
        return Object.keys(this.innerStyles || {}).map(k => `${k} { ${this.innerStyles[k]} }`).join('\n');
    }
    handleCSSColors() {
        let fontColor = Utils.getCSSColor(this.tileElem, '--warp-view-font-color', '#404040');
        fontColor = ((this.innerOptions) || { fontColor }).fontColor || fontColor;
        let bgColor = Utils.getCSSColor(this.tileElem, '--warp-view-bg-color', 'transparent');
        bgColor = ((this.innerOptions) || { bgColor }).bgColor || bgColor;
        const dm = ((this.innerResult || {
            globalParams: { bgColor, fontColor },
        }).globalParams || { bgColor, fontColor });
        this.bgColor = dm.bgColor || bgColor;
        this.fontColor = dm.fontColor || fontColor;
        if (this.tileElem) {
            const rs = getComputedStyle(this.tileElem);
            if ('' === rs.getPropertyValue('--warp-view-font-color').trim()) {
                this.tileElem.style.setProperty('--warp-view-font-color', this.fontColor);
            }
            if ('' === rs.getPropertyValue('--warp-view-chart-label-color').trim()) {
                this.tileElem.style.setProperty('--warp-view-chart-label-color', this.fontColor);
            }
            if ('#8e8e8e' === rs.getPropertyValue('--warp-view-chart-grid-color').trim()) {
                this.tileElem.style.setProperty('--warp-view-chart-grid-color', this.fontColor);
            }
        }
    }
    static get is() { return "discovery-tile-result"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-tile-result.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-tile-result.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "start": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "start",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "''"
            },
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            },
            "chartTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "chart-title",
                "reflect": false
            },
            "chartDescription": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "chart-description",
                "reflect": false
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'warpscript' | 'flows'",
                    "resolved": "\"flows\" | \"warpscript\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "language",
                "reflect": false,
                "defaultValue": "'warpscript'"
            },
            "vars": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "vars",
                "reflect": false,
                "defaultValue": "'{}'"
            }
        };
    }
    static get states() {
        return {
            "execTime": {},
            "bgColor": {},
            "fontColor": {},
            "innerResult": {},
            "innerOptions": {},
            "innerStyle": {},
            "innerType": {},
            "innerTitle": {}
        };
    }
    static get events() {
        return [{
                "method": "discoveryEvent",
                "name": "discoveryEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "DiscoveryEvent",
                    "resolved": "DiscoveryEvent",
                    "references": {
                        "DiscoveryEvent": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DiscoveryEvent"
                        }
                    }
                }
            }, {
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "selfType",
                "name": "selfType",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "setZoom": {
                "complexType": {
                    "signature": "(dataZoom: { start?: number; end?: number; type?: string; }) => Promise<void>",
                    "parameters": [{
                            "name": "dataZoom",
                            "type": "{ start?: number; end?: number; type?: string; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "show": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "showById": {
                "complexType": {
                    "signature": "(id: number) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hideById": {
                "complexType": {
                    "signature": "(id: number) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "(regexp: string, ts: number, value?: number) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "ts",
                            "type": "number",
                            "docs": ""
                        }, {
                            "name": "value",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "unFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<{ dataUrl: string; bgColor: string; }>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ dataUrl: string; bgColor: string; }>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "parseEvents": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "type",
                "methodName": "updateType"
            }, {
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }, {
                "propName": "vars",
                "methodName": "varsUpdate"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "draw",
                "method": "onDrawHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rendered",
                "method": "onDrawHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "leftMarginComputed",
                "method": "onLeftMarginComputed",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "timeBounds",
                "method": "onTimeBounds",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-tile-result.js.map
